'use client';

import { useCallback, useState } from 'react';
import { useAccountUm } from '@/hooks/use-account-um';
import { useWalletUm } from '@/hooks/use-wallet-um';
import { getAppPlatform } from '@/logic/platform';
import { APP_PLATFORM } from '@/utils/constants';
import { useStore } from '@/store/store';
import { POST_LOGIN_ACTION_TYPE, setPostLoginAction } from '@/utils/post-login-action/post-login-action';
import { isGameLauncherUrl } from '@fanduel/rewards-hub-client/promotion-utils';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useLauncherUrlResolution } from '@/hooks/use-launcher-url-resolution';
import { getGameLaunchMode } from '@/utils/url/game-launch-mode';

type MinimalEvent = {
    type?: string;
    href?: string;
    originalHref?: string;
    uri?: string;
    promoCode?: string;
    label?: string;
};
type EventsHandler = (event: MinimalEvent) => Promise<void> | void;
type AnchorEvent = { preventDefault?: () => void } | Event;

export const REFERRER_TYPE_REWARDS = 'rewards';
const isRegisterUrl = (url: string) => url.includes('/register') || url.includes('/join');
const isLoginUrl = (url: string) => url.includes('/login');
const isWalletDepositUrl = (url: string) => url.includes('deposit');
const isResponsibleGamingUrl = (url: string) => url.includes('responsible-play');

export const useCppEventsHandler = (
    referrer: string = 'rewards'
): [EventsHandler, (event: AnchorEvent) => void, boolean, (value: boolean) => void] => {
    const [showError, setShowError] = useState<boolean>(false);
    const isLoggedIn = useStore((s) => s.userContext.isLoggedIn);
    const { isMobile, isDesktop, isAndroidWrapper, isIosWrapper } = useGlobalContext();
    const launcherResolution = useLauncherUrlResolution();
    const { triggerLogin, triggerRegister } = useAccountUm();
    const { triggerDeposit } = useWalletUm();

    const eventsHandler: EventsHandler = useCallback(
        async (event: MinimalEvent) => {
            if (event.type === 'API_ERROR') {
                setShowError(true);
            }

            if (event.type === 'BUTTON_CLICK' && event.href) {
                const { href, originalHref } = event;
                const platform = getAppPlatform();
                let urlReferrer: string | null = null;
                try {
                    urlReferrer = new URL(href, window.location.origin).searchParams.get('external-referrer-next');
                } catch {
                    urlReferrer = null;
                }

                if (!isLoggedIn && (isLoginUrl(href) || isRegisterUrl(href)) && originalHref) {
                    if (isGameLauncherUrl(originalHref)) {
                        setPostLoginAction({
                            type: POST_LOGIN_ACTION_TYPE.LAUNCH_GAME,
                            url: originalHref,
                            launchMode: getGameLaunchMode(originalHref, {
                                ...launcherResolution,
                                isMobile,
                                isAndroidWrapper,
                                isIosWrapper,
                                isDesktop
                            })
                        });
                    } else if (isWalletDepositUrl(originalHref)) {
                        setPostLoginAction({ type: POST_LOGIN_ACTION_TYPE.OPEN_DEPOSIT });
                    }
                }

                if (!isLoggedIn && isLoginUrl(href)) {
                    triggerLogin({ externalReferrer: urlReferrer || referrer });
                }

                if (!isLoggedIn && isRegisterUrl(href)) {
                    triggerRegister({ externalReferrer: urlReferrer || referrer });
                }

                if (isLoggedIn && isWalletDepositUrl(href) && platform === APP_PLATFORM.Web) {
                    await triggerDeposit();
                }

                if (isLoggedIn && isResponsibleGamingUrl(href) && platform === APP_PLATFORM.Web) {
                    // Lazy load ResponsibleGaming to avoid SSR issues
                    const { ResponsibleGaming } = await import('@fanduel/cpe-um-bundle/responsible-gaming/library');
                    await ResponsibleGaming.presentResponsiblePlaySettingsAsync(undefined, true, true);
                }
            }
        },
        [
            isLoggedIn,
            referrer,
            triggerLogin,
            triggerRegister,
            triggerDeposit,
            isMobile,
            isDesktop,
            isAndroidWrapper,
            isIosWrapper,
            launcherResolution
        ]
    );

    const anchorCallback = useCallback(
        (event: AnchorEvent) => {
            if (!isLoggedIn) {
                event?.preventDefault?.();
            }
        },
        [isLoggedIn]
    );

    return [eventsHandler, anchorCallback, showError, (value: boolean) => setShowError(value)];
};
